import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestionItem } from '../../types/interview.types';
import { MonacoIDE } from './MonacoIDE';
import { AIToolsSetupPopup } from './AIToolsSetupPopup';
import { extractCodeFromQuestion, removeCodeBlocksFromQuestion } from '../../utils/questionCodeExtraction';
import { Button } from '../ui/atoms/Button';
import { Tag } from '../ui/atoms/Tag';
import { getInterviewModalityLabel } from '../../utils/interview-modality';

interface QuestionTimelineItemProps {
  question: QuestionItem;
  isActive: boolean;
  isCompleted: boolean;
  timeRemaining: number;
  isLastVisible: boolean; // To control the connecting line
  currentQuestionActivationTime?: number;
  questionDurationMinutes?: number;
  interviewId?: string; // Add interview ID for Monaco state tracking
  // Azure TTS props
  speak?: (text: string) => void;
  stopTTS?: () => void;
  isTTSPlaying?: boolean;
  isMuted?: boolean;
}

export const QuestionTimelineItem: React.FC<QuestionTimelineItemProps> = ({
  question,
  isActive,
  isCompleted,
  timeRemaining,
  isLastVisible,
  currentQuestionActivationTime,
  questionDurationMinutes,
  interviewId,
  speak,
  stopTTS,
  isTTSPlaying = false,
  isMuted = false,
}) => {
  const { t } = useTranslation('interview');
  // Local state for seconds countdown if active
  const [localSeconds, setLocalSeconds] = useState<number | undefined>();
  
  // State for AI tools setup popup
  const [showAIToolsPopup, setShowAIToolsPopup] = useState<boolean>(false);
  const [hasShownPopupForThisQuestion, setHasShownPopupForThisQuestion] = useState<boolean>(false);

  useEffect(() => {
    if (isActive && currentQuestionActivationTime && questionDurationMinutes) {
      // Calculate initial seconds left
      const getSecondsLeft = () => {
        const totalMs = questionDurationMinutes * 60 * 1000;
        const elapsedMs = Date.now() - currentQuestionActivationTime;
        return Math.max(0, Math.ceil((totalMs - elapsedMs) / 1000));
      };
      setLocalSeconds(getSecondsLeft());
      const interval = setInterval(() => {
        setLocalSeconds(getSecondsLeft());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isActive, currentQuestionActivationTime, questionDurationMinutes]);

  // Show AI tools popup when screen_recording question becomes active
  useEffect(() => {
    if (isActive && question.screen_recording && !hasShownPopupForThisQuestion) {
      setShowAIToolsPopup(true);
      setHasShownPopupForThisQuestion(true);
    }
  }, [isActive, question.screen_recording, hasShownPopupForThisQuestion]);

  // Reset popup state when question changes
  useEffect(() => {
    if (!isActive) {
      setHasShownPopupForThisQuestion(false);
      setShowAIToolsPopup(false);
    }
  }, [isActive]);

  // Check if this question needs IDE
  const needsIDE = question.read_ide || question.write_ide;
  const shouldShowIDE = needsIDE && isActive; // Only show IDE when question is active

  // Extract code and language from question if it's a read_ide question
  // Prefer code_snippet from ExamConfig over extracted code from question text
  const { code: extractedCode, language: extractedLanguage } = question.read_ide
    ? extractCodeFromQuestion(question.question)
    : { code: '', language: 'javascript' };

  // Use code_snippet from ExamConfig if available, otherwise fall back to extracted code
  const codeToDisplay = question.read_ide
    ? (question.code_snippet || extractedCode || '')
    : (question.code_snippet || '');

  // Determine programming language — prefer coding_language from ExamConfig
  const getLanguage = (): string => {
    if (question.coding_language) {
      return question.coding_language;
    }
    if (question.read_ide && extractedLanguage) {
      return extractedLanguage;
    }

    switch (question.question_kind) {
      case 'algorithmic':
      case 'debugging':
      case 'clean_code':
        return 'python';
      default:
        return 'python';
    }
  };

  // Get the question text to display (without code blocks for read_ide questions)
  const getDisplayText = (): string => {
    if (question.read_ide) {
      return removeCodeBlocksFromQuestion(question.question);
    }
    return question.question;
  };

  // Handler functions for AI tools popup
  const handleAIToolsPopupClose = () => {
    setShowAIToolsPopup(false);
  };

  const handleAIToolsPopupConfirm = () => {
    setShowAIToolsPopup(false);
    // The question will be visible after popup is closed
  };

  return (
    <div className="flex">
      {/* Timeline dot */}
      <div className="relative flex-shrink-0">
        <div
          className={`w-6 h-6 rounded-full ${
            isCompleted ? 'bg-green-500' : isActive ? 'bg-blue-500 animate-pulse' : 'bg-gray-300'
          } flex items-center justify-center z-10`}
        >
          {isCompleted && (
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        {/* Vertical line connecting to next question (only if not the last visible) */}
        {!isLastVisible && (
          <div className="absolute top-6 left-1/2 -translate-x-1/2 h-full w-0.5 bg-gray-200"></div>
        )}
      </div>

      {/* Question content */}
      <div className="ml-4 flex-grow pb-6">
        <div className={`prose prose-sm max-w-none whitespace-pre-line ${isCompleted ? 'text-gray-400' : 'text-gray-800'}`}>
          {/* Use dangerouslySetInnerHTML or a markdown renderer if questions can contain markdown */}
          {getDisplayText()}
        </div>

        {/* Question type and time indicator */}
        <div className="mt-1 text-sm flex items-center flex-wrap gap-x-2">
          <Tag
            intent={question.question_type === 'technical' ? 'primary' : 'info'}
            variant="subtle"
          >
            {getInterviewModalityLabel(question.question_type, t)}
          </Tag>
          <span className="text-gray-500">
            {question.time === 1
              ? t('live.questions.minute_one', { count: 1 })
              : t('live.questions.minute_other', { count: question.time })}
          </span>
          
          {/* "Hear Question" / "Stop" button — available on all active questions */}
          {isActive && speak && (
            <Button
              type="contained"
              size="xs"
              intent={isTTSPlaying ? 'danger-soft' : 'success-soft'}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isTTSPlaying) {
                  stopTTS?.();
                } else {
                  speak(question.question);
                }
              }}
              disabled={isMuted}
              label={isTTSPlaying ? t('live.questions.stop_question') : t('live.questions.hear_question')}
            />
          )}
          
          {/* IDE indicators */}
          {question.read_ide && (
            <Tag intent="info" variant="subtle">
              📖 {t('live.questions.ide.code_review')}
            </Tag>
          )}
          {question.write_ide && (
            <Tag intent="success" variant="subtle">
              ✏️ {t('live.questions.ide.code_editor')}
            </Tag>
          )}
          
          {question.screen_recording && (
            <Tag intent="warning" variant="subtle">
              🤖 {t('live.questions.ide.ai_tools_required')}
            </Tag>
          )}

          {/* Remaining Time / Status - Color-coded based on time remaining */}
          {isActive && localSeconds !== undefined && localSeconds > 0 ? (
            (() => {
              const totalSeconds = (questionDurationMinutes || question.time) * 60;
              const percentRemaining = (localSeconds / totalSeconds) * 100;
              
              const intent = percentRemaining > 75
                ? 'success'
                : percentRemaining > 50
                  ? 'info'
                  : percentRemaining > 10
                    ? 'warning'
                    : 'error';
              
              const minutes = Math.floor(localSeconds / 60);
              const seconds = localSeconds % 60;
              
              return (
                <Tag
                  intent={intent}
                  variant="subtle"
                  className={`ml-auto ${percentRemaining <= 10 ? 'animate-pulse' : ''}`}
                >
                  {minutes > 0 ? `${minutes}:${seconds.toString().padStart(2, '0')}` : `${seconds}s`}
                </Tag>
              );
            })()
          ) : isActive && timeRemaining > 0 ? (
            <span className="ml-auto font-medium text-blue-600 text-xs flex-shrink-0">
              {timeRemaining}m left
            </span>
          ) : null}
           {isActive && localSeconds !== undefined && localSeconds <= 0 && (
            <Tag intent="error" variant="subtle" className="ml-auto animate-pulse">
              ⏰ Time's Up
            </Tag>
          )}
          {isCompleted && (
            <span className="ml-auto text-sm text-green-600 flex-shrink-0">
              Completed
            </span>
          )}
        </div>

        {/* Monaco IDE - Show only when active and IDE is needed */}
        {shouldShowIDE && (
          <div className="mt-6 w-full overflow-hidden border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-gray-800 px-4 py-2 text-white text-sm font-medium font-heading">
              Code Editor {question.write_ide ? '(Editable)' : '(Read-Only)'}
            </div>
            <div className="w-full min-w-0">
              {question.read_ide && !question.write_ide ? (
                // Code-read-only: show code_snippet (or extracted code) in read-only mode, language fixed
                <MonacoIDE
                  questionIndex={question.index}
                  questionKind={question.question_kind}
                  isReadOnly={true}
                  initialCode={codeToDisplay}
                  language={getLanguage()}
                  showLanguageSelector={false}
                  className="w-full min-w-0"
                  interviewId={interviewId}
                />
              ) : (
                // Code-write: fixed language from config, starter code from code_snippet if provided
                <MonacoIDE
                  questionIndex={question.index}
                  questionKind={question.question_kind}
                  isReadOnly={false}
                  initialCode={question.code_snippet || undefined}
                  language={getLanguage()}
                  showLanguageSelector={false}
                  className="w-full min-w-0"
                  interviewId={interviewId}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* AI Tools Setup Popup */}
      <AIToolsSetupPopup
        isOpen={showAIToolsPopup}
        onClose={handleAIToolsPopupClose}
        onConfirm={handleAIToolsPopupConfirm}
      />
    </div>
  );
}; 